"use client";

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { type CmsLocale, translate } from "./translations";

const STORAGE_KEY = "cms-locale";

interface CmsLocaleContextValue {
  locale: CmsLocale;
  setLocale: (locale: CmsLocale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const CmsLocaleContext = createContext<CmsLocaleContextValue>({
  locale: "de",
  setLocale: () => {},
  t: (key) => key,
});

function getInitialLocale(): CmsLocale {
  if (typeof window === "undefined") return "de";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "de" || stored === "it" || stored === "en") return stored;
  return "de";
}

export function CmsLocaleProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: CmsLocale }) {
  const [locale, setLocaleState] = useState<CmsLocale>(initialLocale ?? "de");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!initialLocale) {
      setLocaleState(getInitialLocale());
    }
    setMounted(true);
  }, [initialLocale]);

  const setLocale = useCallback((newLocale: CmsLocale) => {
    setLocaleState(newLocale);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, newLocale);
    }
    // Also persist to DB via API (fire and forget)
    fetch("/api/auth/locale", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sprache: newLocale }),
    }).catch(() => {});
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      if (!mounted) return translate("de", key, params);
      return translate(locale, key, params);
    },
    [locale, mounted]
  );

  return (
    <CmsLocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </CmsLocaleContext.Provider>
  );
}

export function useT() {
  return useContext(CmsLocaleContext);
}
